import React, { useState, useRef, useEffect } from 'react';
import { HiOutlineShare } from 'react-icons/hi';
import toast from 'react-hot-toast';

export default function ShareButton({ pool, colors, size = 'default' }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  if (!pool) return null;

  const poolUrl = `${window.location.origin}/pool/${pool.id}`;
  const text = `${pool.name} on ${pool.chain} — ${(pool.apr || 0).toFixed(2)}% APR | ${pool.protocol}`;

  const handleCopy = () => {
    navigator.clipboard.writeText(poolUrl).then(() => {
      toast.success('Link copied!', {
        style: { fontFamily: "'IBM Plex Mono', monospace", fontSize: 12 },
        duration: 2000,
      });
    }).catch(() => {
      toast.error('Failed to copy', {
        style: { fontFamily: "'IBM Plex Mono', monospace", fontSize: 12 },
        duration: 2000,
      });
    });
    setOpen(false);
  };

  const shareTwitter = () => {
    const url = `https://x.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(poolUrl)}`;
    window.open(url, '_blank', 'width=600,height=400');
    setOpen(false);
  };

  const shareTelegram = () => {
    const url = `https://t.me/share/url?url=${encodeURIComponent(poolUrl)}&text=${encodeURIComponent(text)}`;
    window.open(url, '_blank', 'width=600,height=400');
    setOpen(false);
  };

  const isSmall = size === 'small';

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 5,
          fontFamily: "'IBM Plex Mono', monospace",
          fontSize: isSmall ? 10 : 11,
          color: colors.textSecondary,
          background: 'transparent',
          border: `1px solid ${colors.border}`,
          borderRadius: 8,
          padding: isSmall ? '4px 8px' : '6px 12px',
          cursor: 'pointer',
        }}
      >
        <HiOutlineShare size={isSmall ? 12 : 14} />
        {!isSmall && 'Share'}
      </button>

      {open && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 6px)',
          right: 0,
          background: colors.surface,
          border: `1px solid ${colors.border}`,
          borderRadius: 10,
          padding: 6,
          boxShadow: `0 8px 24px ${colors.shadow}`,
          zIndex: 100,
          minWidth: 160,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}>
          {[
            { label: 'Copy Link', icon: '\uD83D\uDD17', action: handleCopy },
            { label: 'Share on X', icon: '\uD835\uDD4F', action: shareTwitter },
            { label: 'Telegram', icon: '\u2708', action: shareTelegram },
          ].map(item => (
            <button
              key={item.label}
              onClick={item.action}
              style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 11,
                color: colors.textSecondary,
                background: 'transparent',
                border: 'none',
                borderRadius: 6,
                padding: '8px 10px',
                cursor: 'pointer',
                textAlign: 'left',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                width: '100%',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.background = colors.hover;
                e.currentTarget.style.color = colors.textPrimary;
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = colors.textSecondary;
              }}
            >
              <span style={{ fontSize: 14 }}>{item.icon}</span>
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
